import React, { useEffect, useState } from "react";
import { Box, Typography, Dialog, DialogContent, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useBackButtonHandler } from "../../hooks/useBackButtonHandler";

const ease = [0.22, 1, 0.36, 1] as const;

const LoadingDividers: React.FC = () => (
  <Box sx={{ position: "relative", width: 200, height: 40 }}>
    {/* Vertical centre line */}
    <motion.div
      animate={{ scaleY: [0, 1, 1, 0] }}
      transition={{ duration: 2, repeat: Infinity, ease, times: [0, 0.2, 0.8, 1] }}
      style={{
        position: "absolute", left: "50%", top: 0,
        width: 1, height: "100%",
        backgroundColor: "rgba(29,47,65,0.25)",
        transformOrigin: "top",
      }}
    />
    {/* Left horizontal line */}
    <motion.div
      animate={{ scaleX: [0, 1, 1, 0] }}
      transition={{ duration: 2, repeat: Infinity, ease, times: [0, 0.3, 0.8, 1], delay: 0.15 }}
      style={{
        position: "absolute", top: "50%", right: "calc(50% + 6px)",
        width: "calc(50% - 6px)", height: 1,
        backgroundColor: "rgba(29,47,65,0.2)",
        transformOrigin: "right",
      }}
    />
    {/* Right horizontal line */}
    <motion.div
      animate={{ scaleX: [0, 1, 1, 0] }}
      transition={{ duration: 2, repeat: Infinity, ease, times: [0, 0.3, 0.8, 1], delay: 0.15 }}
      style={{
        position: "absolute", top: "50%", left: "calc(50% + 6px)",
        width: "calc(50% - 6px)", height: 1,
        backgroundColor: "rgba(29,47,65,0.2)",
        transformOrigin: "left",
      }}
    />
  </Box>
);

const CALENDLY_URL = "https://calendly.com/alen-stepic-kanonics/30min?background_color=f4f6f9&text_color=1d2f41&primary_color=1d2f41";

interface Props {
  open: boolean;
  onClose: () => void;
  calendlyId?: string;
}

const ContactMeetingDialog: React.FC<Props> = ({ open, onClose, calendlyId = "calendly-inline" }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  // Handle back button to close modal
  useBackButtonHandler(open, onClose, "meeting-dialog");

  useEffect(() => {
    if (!document.querySelector('script[src*="calendly.com"]')) {
      const s = document.createElement("script");
      s.src = "https://assets.calendly.com/assets/external/widget.js";
      s.async = true;
      document.head.appendChild(s);
    }
  }, []);

  useEffect(() => { if (open) setLoading(true); }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MessageEvent) => {
      if (e.origin === "https://calendly.com" && (e.data as any)?.event === "calendly.event_type_viewed") {
        setLoading(false);
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [open]);

  const initCalendly = () => {
    const el = document.getElementById(calendlyId);
    if (!el) return;
    const calendly = (window as any).Calendly;
    if (calendly) { el.innerHTML = ""; calendly.initInlineWidget({ url: CALENDLY_URL, parentElement: el }); }
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen TransitionProps={{ onEntered: initCalendly }} sx={{ zIndex: 99999 }} PaperProps={{ sx: { borderRadius: 0, bgcolor: "#f8f9fb" } }}>
      <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column", overflow: "hidden", height: "100%" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 4, py: 2.5, borderBottom: "1px solid rgba(29,47,65,0.12)", flexShrink: 0 }}>
          <Typography sx={{ fontSize: "0.65rem", fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", fontFamily: '"Calibri", sans-serif', color: "#1D2F41", bgcolor: "#EBEDF0", display: "inline-block", px: 1.25, py: 0.75 }}>
            {t("contact.meeting.dialogTitle")}
          </Typography>
          <IconButton onClick={onClose} aria-label={t("nav.closeDialog")} sx={{ color: "rgba(29,47,65,0.4)" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, position: "relative", minWidth: "320px", overflow: "hidden" }}>
          <Box id={calendlyId} sx={{ width: "100%", height: "100%" }} />
          <Box sx={{
            position: "absolute", inset: 0, bgcolor: "#f8f9fb",
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2.5,
            opacity: loading ? 1 : 0,
            pointerEvents: loading ? "auto" : "none",
            transition: "opacity 0.5s ease",
          }}>
            <Typography sx={{ fontSize: "0.65rem", fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", fontFamily: '"Calibri", sans-serif', color: "rgba(29,47,65,0.4)" }}>
              {t("contact.form.loading")}
            </Typography>
            <LoadingDividers />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ContactMeetingDialog;
